"use client";

import React from "react";
import Image from "next/image";

// Helper to render FontAwesome icon or image
const RenderIcon = ({ iconStr, className }) => {
  if (!iconStr || typeof iconStr !== 'string') {
    return <i className={`fa-solid fa-rocket ${className}`}></i>;
  }
  
  const isUrl = iconStr.startsWith('http') || iconStr.startsWith('/') || iconStr.includes('uploads');
  
  if (isUrl) {
    return (
      <Image
        src={iconStr}
        alt="Service Excellence Icon"
        width={48}
        height={48}
        className={className + " object-contain"}
      />
    );
  }

  // Ensure FontAwesome classes are correct (e.g. "fa-solid fa-code")
  const iconClass = iconStr.includes(' ') ? iconStr : `fa-solid fa-${iconStr}`;
  return <i className={`${iconClass} ${className}`}></i>;
};

// Accent gradients cycled per card
const ACCENTS = [
  "from-orange-500 to-[#f1833b]",
  "from-[#f1833b] to-amber-500",
  "from-amber-500 to-orange-500",
];

// Static fallback cards
const STATIC_CARDS = [
  {
    icon: "rocket",
    title: "High Performance Output",
    description: "Speed optimized architecture delivering instantaneous load capabilities and fluid interactivity.",
  },
  {
    icon: "shield",
    title: "Enterprise-Grade Security",
    description: "Encrypted safeguards deployed across database queries, client state, and server infrastructure.",
  },
  {
    icon: "smile",
    title: "24/7 Premium Support",
    description: "Strategic availability cycles that proactively monitor and maintain your critical operations.",
  },
];



export default function WhyChooseUs({ techLabel, whyChoose }) {
  const title = whyChoose?.title || `Unmatched Quality in ${techLabel} Engineering.`;
  const description = whyChoose?.description || "Our global expertise delivers high-grade architectural capability, unmatched speeds, and seamless application scaling protocols.";

  const rawCards = whyChoose?.cards?.filter(c => c.title) || [];
  const cards = rawCards.length > 0 ? rawCards : STATIC_CARDS;

  const developerCard = whyChoose?.developerCard || {};
  const devTitle = developerCard.title || "120+ Developers";
  const devDesc = developerCard.description || "Vetted full-stack experts on demand";
  const devImages = (developerCard.image && developerCard.image.length > 0) 
    ? developerCard.image 
    : [
        "/assets/img/devs/dev1.jpg",
        "/assets/img/devs/dev2.jpg",
        "/assets/img/devs/dev3.jpg"
      ];

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:items-center">

        {/* Left Column - Typography & Highlights */}
        <div className="lg:col-span-5 space-y-6">
          <span className="text-xs font-bold tracking-widest text-[#f48227] uppercase bg-transparent px-4 py-1.5 rounded-full" style={{ border: "1px solid #f48227" }}>
            Why Choose Us
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white leading-tight">
            {title}
          </h2>
          <p className="text-gray-400 text-sm sm:text-base leading-relaxed max-w-md">
            {description}
          </p>

          {/* Mini Stats widget */}
          <div className="p-6 bg-white/5 rounded-3xl backdrop-blur-md flex items-center gap-6 mt-8 shadow-xl shadow-black/20">
            <div className="flex -space-x-3">
              {devImages.slice(0, 3).map((img, idx) => (
                <Image 
                  key={idx}
                  className="w-10 h-10 rounded-full border-2 border-[#1a1a1a] object-cover" 
                  src={img} 
                  alt={`Dev ${idx + 1}`} 
                  width={40}
                  height={40}
                />
              ))}
            </div>
            <div>
              <div className="text-lg font-bold text-white">{devTitle}</div>
              <div className="text-xs text-gray-400">{devDesc}</div>
            </div>
          </div>
        </div>

        {/* Right Column - Dynamic Card Grid */}
        <div className="lg:col-span-7 flex flex-col gap-6 md:gap-8">
          {cards.map((card, i) => {
            const accent = ACCENTS[i % ACCENTS.length];
            return (
              <div
                key={i}
                className="group relative flex flex-col md:flex-row gap-6 p-6 md:p-8 bg-gradient-to-r from-white/10 to-white/5 rounded-3xl ring-2 ring-zinc-800 backdrop-blur-md hover:ring-[#f1833b]/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(241,131,59,0.08)] shadow-xl shadow-black/20 overflow-hidden"
              >
                {/* Visual Accent Border */}
                <div className={`absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b ${accent} rounded-l-3xl opacity-60 group-hover:opacity-100 transition-opacity duration-300`}></div>

                {/* Icon wrapper */}
                <div className="w-14 h-14 flex-shrink-0 bg-[#f1833b]/10 rounded-2xl flex items-center justify-center text-[#f1833b] group-hover:bg-[#f1833b] group-hover:text-white group-hover:rotate-3 transition-all duration-500 shadow-inner">
                  <RenderIcon iconStr={card.icon} className="text-2xl" />
                </div>

                {/* Card Details */}
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-white group-hover:text-[#f1833b] transition-colors duration-300">
                    {card.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed max-w-xl">
                    {card.description || card.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
